import React, { useState, useEffect } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import { Grid, GridColumn as Column, GridCellProps } from "@progress/kendo-react-grid";
import {
  IExpansionAccount,
  IExpansionAccountsResponse,
  ExpansionMovementType,
  ExpansionBasis,
} from "src/interfaces/Reports/IExpansion";
import { formatCurrency, formatSignedCurrency, escCsv } from "./format";

interface Props {
  month: string; // "yyyy-MM"
  monthLabel: string; // e.g. "Mar 2026"
  basis: ExpansionBasis; // which revenue basis the parent grid is showing
  factor: number; // 1 for MRR, 12 for ARR
  unitLabel: string; // "MRR" or "ARR"
  onClose: () => void;
}

const MOVEMENT_TABS: { type: ExpansionMovementType; label: string }[] = [
  { type: "expansion", label: "Expanded" },
  { type: "contraction", label: "Contracted" },
  { type: "churn", label: "Churned" },
  { type: "new", label: "New" },
];

const ExpansionAccountsModal: React.FC<Props> = ({ month, monthLabel, basis, factor, unitLabel, onClose }) => {
  const [type, setType] = useState<ExpansionMovementType>("expansion");
  const [accounts, setAccounts] = useState<IExpansionAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response: IExpansionAccountsResponse = await callApi(
          `reports/expansion/accounts?month=${month}&type=${type}&basis=${basis}`,
          HttpMethod.GET,
        );
        if (cancelled) return;
        setAccounts(response.data || []);
      } catch (err: any) {
        if (cancelled) return;
        setError(`Error loading accounts: ${err.message || err}`);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [month, type, basis]);

  const handleDownloadCsv = () => {
    if (accounts.length === 0) return;
    const headers = ["Account", "Account ID", `Prior ${unitLabel}`, `Current ${unitLabel}`, "Change"];
    const rows = accounts.map((a) => [
      escCsv(a.accountName || ""),
      escCsv(a.acctId || ""),
      String(a.priorMrr * factor),
      String(a.currentMrr * factor),
      String(a.delta * factor),
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `expansion-${basis}-${unitLabel.toLowerCase()}-${type}-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const CurrencyCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!field) return <td />;
    return <td className="text-end">{formatCurrency((dataItem[field] ?? 0) * factor)}</td>;
  };

  const DeltaCell: React.FC<GridCellProps> = (props) => {
    const value = props.dataItem.delta as number;
    const cls = value > 0 ? "text-success" : value < 0 ? "text-danger" : "";
    return <td className={`text-end fw-semibold ${cls}`}>{formatSignedCurrency(value * factor)}</td>;
  };

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Accounts — {monthLabel}</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <ul className="nav nav-pills">
                  {MOVEMENT_TABS.map((tab) => (
                    <li className="nav-item" key={tab.type}>
                      <button
                        className={`nav-link ${type === tab.type ? "active" : ""}`}
                        onClick={() => setType(tab.type)}
                      >
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleDownloadCsv}
                  disabled={accounts.length === 0}
                >
                  <i className="bi bi-download me-1" />
                  Download CSV
                </button>
              </div>

              {error && <div className="alert alert-danger">{error}</div>}

              {isLoading ? (
                <div className="text-center p-4">
                  <span className="spinner-border" role="status" />
                </div>
              ) : accounts.length === 0 ? (
                <p className="text-muted mb-0">No accounts in this category for {monthLabel}.</p>
              ) : (
                <Grid data={accounts} style={{ maxHeight: "60vh" }}>
                  <Column field="accountName" title="Account" />
                  <Column field="acctId" title="Account ID" width={140} />
                  <Column field="priorMrr" title={`Prior ${unitLabel}`} width={140} cell={CurrencyCell} />
                  <Column field="currentMrr" title={`Current ${unitLabel}`} width={140} cell={CurrencyCell} />
                  <Column field="delta" title="Change" width={140} cell={DeltaCell} />
                </Grid>
              )}
            </div>
            <div className="modal-footer">
              <span className="text-muted me-auto small">
                {accounts.length} account{accounts.length === 1 ? "" : "s"}
              </span>
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default ExpansionAccountsModal;
